import React, {useState, useReducer, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {isEmpty} from 'lodash/lang';
import {startCase} from 'lodash/string';
import SnapshotForm from './SnapshotForm';
import DataExportForm from './DataExportForm';
import HelpForm from './HelpForm';
import {INSTRUMENT_MAP} from './utils';
import ActionButton from './ActionButton';
import SnapshotAndDataExportDropdown from './SnapshotAndDataExportDropdown';
import snapshotWhite from '../images/snapshot-white.svg';
import snapshotBlue from '../images/snapshot-blue.svg';
import dataExportWhite from '../images/data-export-white.svg';
import dataExportBlue from '../images/data-export-blue.svg';
import helpWhite from '../images/help-white.svg';
import helpBlue from '../images/help-blue.svg';

const NOT_CONNECTED = 'Not connected';

const ToolBar = React.forwardRef(
  ({isPopoutMode, isSimulateMode, instruments, setInstrumentsTabLoaded, setDeviceTabLoaded, isLargeScreen}, ref) => {
    const [{deviceState, connectedDevice}, setDeviceState] = useState({});
    const [deviceNameInPopoutMode, setDeviceNameInPopoutMode] = useState(NOT_CONNECTED);
    const [instrumentsStatus, dispatch] = useReducer((state, action) => {
      if (action.type === 'SET_STATUS') {
        const {name, status} = action;
        return {
          ...state,
          [name]: status,
        };
      }
      return state;
    }, {});
    const handleInstrumentsStatusChanged = ({detail: {name, status}}) => {
      if (['starting', 'attached'].includes(status)) {
        dispatch({
          type: 'SET_STATUS',
          name,
          status,
        });
      }
    };
    const hasInstruments = !isEmpty(instruments.large) || !isEmpty(instruments.small);
    const deviceConnected = !!(
      (connectedDevice && connectedDevice.deviceName) ||
      (isPopoutMode && deviceNameInPopoutMode !== NOT_CONNECTED)
    );

    const deviceTabRef = useRef();
    const instrumentButtonRef = useRef();
    const handleDeviceConnectionStateChanged = ({detail: {state: deviceState, connectedDevice}}) =>
      setDeviceState({deviceState, connectedDevice});
    const handleInstrumentsTabLoaded = () => setInstrumentsTabLoaded(true);
    const handleDeviceTabLoaded = () => setDeviceTabLoaded(true);
    const handleDeviceNameChanged = (p_event) => {
      const deviceName = p_event.detail.deviceName;
      setDeviceNameInPopoutMode(deviceName);
    };

    useEffect(() => {
      if (isPopoutMode) {
        handleInstrumentsTabLoaded();
      } else {
        if (deviceTabRef.current.isAttached) {
          handleDeviceTabLoaded();
        } else {
          deviceTabRef.current.addEventListener('isAttached', handleDeviceTabLoaded);
        }
        if (deviceTabRef.current.isAttached && deviceTabRef.current.$.deviceTab.connectionState === 'CONNECTED') {
          handleDeviceConnectionStateChanged({
            bubbles: true,
            cancelable: true,
            composed: true,
            detail: { state: 'CONNECTED', connectedDevice: deviceTabRef.current.$.deviceTab.manager.activeConnection.deviceInfo},
          });
        } else {
          deviceTabRef.current.addEventListener('connectionStateChanged', handleDeviceConnectionStateChanged);
        }
        if (instrumentButtonRef.current.isAttached) {
          handleInstrumentsTabLoaded();
        } else {
          instrumentButtonRef.current.addEventListener('isAttached', handleInstrumentsTabLoaded);
        }
        instrumentButtonRef.current.addEventListener('instrumentStatusChanged', handleInstrumentsStatusChanged);
        return () => {
          deviceTabRef.current.removeEventListener('isAttached', handleDeviceTabLoaded);
          deviceTabRef.current.removeEventListener('connectionStateChanged', handleDeviceConnectionStateChanged);
          instrumentButtonRef.current.removeEventListener('isAttached', handleInstrumentsTabLoaded);
          instrumentButtonRef.current.removeEventListener('instrumentStatusChanged', handleInstrumentsStatusChanged);
        };
      }
    }, []);

    useEffect(
      () => {
        if (isPopoutMode && ref && ref.current && hasInstruments) {
          const onlyInstrumentInPopoutMode = [...instruments.large, ...instruments.small][0];
          const instrumentsWrapper = ref.current.closest('.bentobox-wrapper').querySelector('.instruments-wrapper');
          const sfpDOMElement = instrumentsWrapper.querySelector(INSTRUMENT_MAP[onlyInstrumentInPopoutMode.name]);
          if (sfpDOMElement) {
            const currentName = sfpDOMElement.TranslatedDeviceName;
            if (currentName) {
              // It is possible that sfp already has the device name before we add the event handler.
              // So we set it here before adding the event handler.
              setDeviceNameInPopoutMode(currentName);
            }
            sfpDOMElement.addEventListener('deviceNameChanged', handleDeviceNameChanged);
            return () => {
              sfpDOMElement.removeEventListener('deviceNameChanged', handleDeviceNameChanged);
            };
          }
        }
      },
      [hasInstruments],
    );

    const getTitle = () => {
      if (isPopoutMode) {
        return deviceNameInPopoutMode;
      }

      return (
        (isEmpty(connectedDevice) ? null : connectedDevice.deviceName) ||
        (deviceState && deviceState === 'CONNECTING'
          ? startCase(deviceState.toLowerCase())
          : isLargeScreen
          ? 'No device connected'
          : 'No device')
      );
    };

    const isActionDisabled = !hasInstruments || Object.values(instrumentsStatus).includes('starting');

    const noDataExport = isPopoutMode && isEmpty(instruments.large) && !isEmpty(instruments.small);
    let snapshotAndDataExport;
    if (noDataExport) {
      // when the data export button is invisible, we don't need to use ellipsis button
      snapshotAndDataExport = (
        <ActionButton
          imageUrl={`${window.NationalInstruments.bentoBox.svgSpriteUrl}${snapshotWhite.id}`}
          activeImageUrl={`${window.NationalInstruments.bentoBox.svgSpriteUrl}${snapshotBlue.id}`}
          disabled={isActionDisabled}
          childComponent={SnapshotForm}
          childProps={{
            instruments,
            toolbarRef: ref,
            deviceConnected,
          }}
        />
      );
    } else {
      snapshotAndDataExport = (
        <SnapshotAndDataExportDropdown>
          <ActionButton
            imageUrl={`${window.NationalInstruments.bentoBox.svgSpriteUrl}${snapshotWhite.id}`}
            activeImageUrl={`${window.NationalInstruments.bentoBox.svgSpriteUrl}${snapshotBlue.id}`}
            disabled={isActionDisabled}
            childComponent={SnapshotForm}
            childProps={{
              instruments,
              toolbarRef: ref,
              deviceConnected,
            }}
          />
          <ActionButton
            imageUrl={`${window.NationalInstruments.bentoBox.svgSpriteUrl}${dataExportWhite.id}`}
            activeImageUrl={`${window.NationalInstruments.bentoBox.svgSpriteUrl}${dataExportBlue.id}`}
            disabled={isActionDisabled}
            childComponent={DataExportForm}
            childProps={{
              instruments,
              toolbarRef: ref,
              deviceConnected,
              isSimulateMode,
            }}
          />
        </SnapshotAndDataExportDropdown>
      );
    }
    let instrumentButton;
    instrumentButton = isSimulateMode ? (
      <simulate-instruments-button
        ref={instrumentButtonRef}
        name="instruments"
        deviceGroup="default"
        id="instrument1"
        className="buttons"
        bentoboxmode="true"
      />
    ) : (
      <instruments-button
        ref={instrumentButtonRef}
        name="instruments"
        deviceGroup="default"
        id="instrument1"
        className="buttons"
        bentoboxmode="true"
      />
    );
    return (
      <div ref={ref} className="toolbar">
        <span className="title">{getTitle()}</span>
        <div className="buttons">
          <div className="group-left">
            {!isPopoutMode && (
              <device-button
                ref={deviceTabRef}
                style={{display: isSimulateMode ? 'none' : 'block'}}
                name="device"
                deviceGroup="default"
                id="device1"
                className="buttons"
                bentoboxmode
              />
            )}
          </div>
          <div className="group-right">
            {!isPopoutMode && instrumentButton}
            {snapshotAndDataExport}
            <ActionButton
              imageUrl={`${window.NationalInstruments.bentoBox.svgSpriteUrl}${helpWhite.id}`}
              activeImageUrl={`${window.NationalInstruments.bentoBox.svgSpriteUrl}${helpBlue.id}`}
              disabled={!hasInstruments}
              childComponent={HelpForm}
              childProps={{
                instruments,
                toolbarRef: ref,
                deviceConnected,
                isSimulateMode,
              }}
            />
          </div>
        </div>
      </div>
    );
  },
);

ToolBar.propTypes = {
  isPopoutMode: PropTypes.bool.isRequired,
  instruments: PropTypes.object.isRequired,
  setInstrumentsTabLoaded: PropTypes.func.isRequired,
  setDeviceTabLoaded: PropTypes.func.isRequired,
  isLargeScreen: PropTypes.bool.isRequired,
};

ToolBar.displayName = 'ToolBar';

export default ToolBar;
